import React, { useState } from "react";
import { motion } from "framer-motion";

const ABOUT_IMG =
  "https://images.pexels.com/photos/35918469/pexels-photo-35918469.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920";

const tabs = [
  {
    id: "vision",
    label: "Our Vision",
    body:
      "Green Signs was founded on a singular conviction — that advertising deserves to be elegant, considered, and placed where attention has weight. We craft a network that elevates the experience of brand discovery rather than interrupts it.",
  },
  {
    id: "difference",
    label: "What Sets Us Apart",
    body:
      "We do not chase footfall. We curate it. Every screen in our network sits inside an environment of consequence — the eighteenth fairway, the members' lounge, the valet stand — where dwell time is measured in minutes and intent is measured in decisions.",
  },
  {
    id: "quality",
    label: "Quality & Craft",
    body:
      "From hardware specification to creative governance, our installations meet a global standard. The result is a media surface worthy of the brands it carries — and an environment worthy of the audiences it reaches.",
  },
];

const ease = [0.22, 1, 0.36, 1];

const About = () => {
  const [active, setActive] = useState(tabs[0].id);
  const current = tabs.find((t) => t.id === active);

  return (
    <section id="about" data-testid="about-section" className="relative py-28 md:py-40 bg-ivory">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Left column */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease }}
          className="lg:col-span-5"
        >
          <span className="eyebrow flex items-center gap-3" data-testid="about-eyebrow">
            <span className="w-10 h-px bg-brass" />
            About Green Signs
            <span className="hidden md:inline-flex items-center text-brass/60">◆</span>
          </span>
          <h2 className="h-display text-4xl md:text-6xl mt-6" data-testid="about-title">
            Advertising,
            <br />
            <span className="italic text-brass">redefined</span> for the
            considered audience.
          </h2>

          <p className="mt-8 text-forest-600 text-base md:text-lg font-light leading-relaxed max-w-xl">
            We redefine the landscape of out-of-home advertising through a
            premium programmatic network in India's most prestigious
            venues. Our work is to give brands unhurried access to the audiences
            who matter — and to give those audiences something worth looking at.
          </p>

          <div className="mt-12 relative h-72 md:h-96 overflow-hidden hairline">
            <img
              src={ABOUT_IMG}
              alt="Golf course at golden hour"
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="about-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ivory/40 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
              <div className="font-serif italic text-ivory text-2xl drop-shadow-[0_2px_8px_rgba(26,40,32,0.6)]">
                "Where attention has weight."
              </div>
            </div>
          </div>
        </motion.div>

        {/* Right column — tabs */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease, delay: 0.15 }}
          className="lg:col-span-7 lg:pl-8 lg:border-l lg:border-forest-800/10"
        >
          <div className="flex flex-col gap-1" role="tablist" data-testid="about-tabs">
            {tabs.map((t) => {
              const isActive = active === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`about-tab-${t.id}`}
                  onClick={() => setActive(t.id)}
                  className={`group flex items-center justify-between text-left py-6 border-b border-forest-800/10 transition-colors duration-500 ${
                    isActive ? "text-forest-900" : "text-forest-500 hover:text-forest-900"
                  }`}
                  role="tab"
                  aria-selected={isActive}
                >
                  <span className="font-serif text-2xl md:text-4xl tracking-tight">
                    {t.label}
                  </span>
                  <span
                    className={`h-px transition-all duration-500 ${
                      isActive ? "bg-brass w-24" : "bg-forest-500/40 w-12"
                    }`}
                  />
                </button>
              );
            })}
          </div>

          <motion.div
            key={active}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease }}
            className="mt-10 max-w-xl"
            data-testid="about-tab-content"
          >
            <p className="text-forest-600 text-base md:text-lg leading-relaxed font-light">
              {current.body}
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
